<template>
    <div>
        <span class="relative flex">
            <h1 class="pt-6 font-medium text-left text-gray-900">
                {{ $t("device.header.wifiConnection") }}
            </h1>
        </span>

        <dd class="mt-1 mb-4 text-sm text-gray-900" ref="wifiConfigContainer">
            <div class="flex flex-col w-full mx-auto mt-4">
                <label for="networkSelection" class="block ml-2 text-sm font-medium text-gray-700">
                    {{ $t("alarms.networkSelection") }}
                </label>

                <div v-if="isEditAllowed" class="flex w-full" ref="wifiConfigCombobox">
                    <Combobox v-model="selectedNetwork" class="relative flex-1 mt-2" as="div">
                        <ComboboxInput :disabled="availableNetworks.length === 0" class="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-inset sm:text-sm sm:leading-6 ring-gray-300 focus:ring-gray-300 focus:border-gray-300" autocomplete="off" :placeholder="availableNetworks.length === 0 ? $t('alarms.noNetworkAvailable') : $t('alarms.networkSelection')" :display-value="(network) => network?.ssid" />

                        <ComboboxButton class="absolute inset-y-0 right-0 flex items-center px-2 rounded-r-md focus:outline-none">
                            <ChevronDownIcon class="w-5 h-5 text-gray-400" aria-hidden="true" />
                        </ComboboxButton>

                        <ComboboxOptions v-if="availableNetworks.length > 0" class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            <ComboboxOption v-for="network in availableNetworks" :key="network?.ssid" :value="network" as="template" v-slot="{ active, selected }">
                                <li :class="[
                                    'relative cursor-default select-none py-1 pl-3 pr-9',
                                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                                ]">
                                    <span :class="['block truncate', selected && 'font-semibold']">
                                        {{ network?.ssid }}
                                    </span>
                                    <span v-if="selected" :class="[
                                        'absolute inset-y-0 right-0 flex items-center pr-4',
                                        active ? 'text-white' : 'text-indigo-600',
                                    ]">
                                        <CheckIcon class="w-5 h-5" aria-hidden="true" />
                                    </span>
                                </li>
                            </ComboboxOption>
                        </ComboboxOptions>
                    </Combobox>

                    <button v-if="selectedNetwork && isEditAllowed" class="flex items-center justify-center ml-1 text-gray-400 !cursor-pointer" @click="removeWifiConfig">
                        <MinusCircleIcon class="w-6 h-6 text-gray-500 cursor-pointer " aria-hidden="true" />
                    </button>
                </div>
                <div v-if=!isEditAllowed class="flex w-full">
                    <input disabled type="text" :value="selectedNetwork?.ssid || $t('alarms.noNetwork')" class="w-full h-8 mt-2 font-mono text-gray-400 break-words border-gray-300 rounded-md shadow-sm" ref="wifiInput" />
                </div>
            </div>

            <div v-if="selectedNetwork && isEditAllowed" class="flex w-full mx-auto mt-2">
                <div :class='["flex w-full mr-1 border border-gray-300 rounded-md shadow-sm", (pwd.length < 8 || pwd.length > 63) ? "border-red-500 focus:border-red-600 focus:ring-red-500" : "", passwordFocused && " border-indigo-500 focus:border-indigo-500 focus:ring-indigo-500"]'>
                    <input v-if="showPassword" :placeholder="$t('alarm.form.password')" v-model="pwd" type="text" class='w-full h-8 mr-1 font-mono break-words border-gray-300 border-none rounded-md shadow-sm focus:ring-0 focus:ring-offset-0' ref="passwordField" @focus="passwordFocused = true" @blur="passwordFocused = false" />
                    <input v-else :placeholder="$t('alarm.form.password')" v-model="pwd" type="password" class='w-full h-8 mr-1 font-mono break-words border-gray-300 border-none rounded-md focus:ring-0 focus:ring-offset-0' ref="passwordField" @focus="passwordFocused = true" @blur="passwordFocused = false" />

                    <button class="mr-2" @click="showPassword = !showPassword">
                        <EyeIcon v-if="showPassword" class="w-6 h-6 text-gray-500 cursor-pointer" aria-hidden="true" />
                        <EyeOffIcon v-else class="w-6 h-6 text-gray-500 cursor-pointer" aria-hidden="true" />
                    </button>
                </div>

                <div class="w-6"></div>
            </div>
            <div v-if="selectedNetwork && isEditAllowed && pwd.length" class="h-6 pb-3 pl-1 mt-2 mb-2">
                <div class="flex items-center text-sm text-red-500" v-if="pwd.length < 8 || pwd.length > 63">
                    <ExclamationCircleIcon class="w-5 h-5" aria-hidden="true" />
                    <p v-if="pwd.length < 8" class="ml-4">{{ $t('alarms.pwdLength') }}</p>
                    <p v-if="pwd.length > 63" class="ml-4">{{ $t('alarms.pwdLengthMax') }}</p>
                </div>
            </div>
        </dd>
    </div>
</template>

<script>
import { Combobox, ComboboxOption, ComboboxButton, ComboboxOptions, ComboboxInput } from "@headlessui/vue";
import { CheckIcon, ChevronDownIcon, MinusCircleIcon, EyeIcon, EyeOffIcon, ExclamationCircleIcon } from "@heroicons/vue/outline";
import { hasDeviceTenantPermission, USER_PERMISSIONS } from "@/lib/userHasPermission";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

export default {
    name: "WifiConfig",
    components: {
        Combobox,
        ComboboxOption,
        ComboboxOptions,
        ComboboxInput,
        ComboboxButton,
        CheckIcon,
        ChevronDownIcon,
        MinusCircleIcon,
        EyeIcon,
        EyeOffIcon,
        ExclamationCircleIcon
    },

    props: ["refresh"],
    emits: ["updateConfig"],

    setup(props, { emit }) {
        const store = useStore();
        const device = computed(() => store.state.device.current);

        const isEditAllowed = computed(() => hasDeviceTenantPermission(USER_PERMISSIONS.ADMIN));

        const availableNetworks = ref(
            Array.from(device.value.stateAlarms.device_state.network.wifi.available_networks)
                .filter(network => network?.ssid && network?.ssid.length > 0)
        );

        const selectedNetwork = ref(
            device.value.stateAlarms.device_state.network.wifi.connected_network ||
            device.value.configV2.system.wifi?.networks?.[0] || null
        )

        const pwd = ref(device.value.configV2.system.wifi?.networks?.[0]?.pre_shared_key || "");
        const isResetting = ref(false);
        const showPassword = ref(false);
        const passwordFocused = ref(false);

        watch(() => props.refresh, () => {
            if (props.refresh) {
                reset()
            }
        })

        watch(selectedNetwork, (oldValue, newValue) => {
            if (!newValue) return;

            if (isResetting.value) {
                isResetting.value = false;
                return;
            }

            if (oldValue && oldValue != newValue) {
                pwd.value = "";
            }
            updateConfigHandler(newValue, pwd.value);
        });

        watch(pwd, (newValue) => {
            updateConfigHandler(selectedNetwork.value, newValue);
        });

        const reset = () => {
            isResetting.value = true;
            selectedNetwork.value = device.value.configV2.system.wifi?.networks?.[0] || null;
            pwd.value = selectedNetwork.value ? selectedNetwork.value.pre_shared_key || "" : "";
        }

        const removeWifiConfig = () => {
            isResetting.value = true;
            selectedNetwork.value = null;
            pwd.value = "";

            updateConfigHandler(null, "");
        };

        const updateConfigHandler = (network, password) => {
            emit("updateConfig", { network: network, password: password });
        };


        return {
            selectedNetwork,
            availableNetworks,
            pwd,
            updateConfigHandler,
            removeWifiConfig,
            isEditAllowed,
            showPassword,
            passwordFocused
        };
    },
};
</script>
